import React from "react"
import { Link } from "react-router-dom"
import SEO from "../../components/SEO.jsx"
import PageHero from "../../components/page/PageHero.jsx"

export default function LandingTemplate({ page }) {
  if (!page) {
    return (
      <div className="min-h-screen bg-white text-gray-900 px-6 pt-32">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-blue-900 mb-2">Landing page not found</h1>
          <p className="text-sm text-gray-700 mb-6">
            This URL does not match any path in landingPages.js.
          </p>
          <Link
            to="/"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-blue-700 transition"
          >
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  const CTA_PRIMARY = page?.ctaPrimary || "Book a Free Website Health Check"
  const CTA_SECONDARY = page?.ctaSecondary || "Start Your Website Project →"
  const CTA_TO = page?.ctaTo || "/contact"
  // Optional: lets the secondary CTA go somewhere different (falls back to CTA_TO).
  const CTA_SECONDARY_TO = page?.ctaSecondaryTo || CTA_TO

  const focusRing =
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"

  return (
    <div className="bg-white text-brand-text">
      <SEO
        title={page.title || "2merative Web Design"}
        description={page.description || ""}
        path={page.path || "/"}
        image={page.image || "/og-image.png"}
      />

      {/* App.js already provides <main>; avoid nesting landmarks. */}
      <div>
        {/* HERO (shared brand hero; per-page CTAs and note passed as children) */}
        <PageHero title={page.heroHeading || "Landing Page"} lead={page.heroSubheading || ""} showCtas={false}>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to={CTA_TO}
              className={`inline-flex min-h-[48px] items-center justify-center rounded-full bg-white px-7 py-3 font-semibold text-brand-navy transition hover:bg-blue-50 focus-visible:ring-white focus-visible:ring-offset-brand-navy ${focusRing}`}
            >
              {CTA_PRIMARY}
            </Link>
            <Link
              to={CTA_SECONDARY_TO}
              className={`inline-flex min-h-[48px] items-center justify-center rounded-full border border-white/40 px-7 py-3 font-semibold text-white transition hover:bg-white/10 focus-visible:ring-white focus-visible:ring-offset-brand-navy ${focusRing}`}
            >
              {CTA_SECONDARY}
            </Link>
          </div>

          <p className="hero-note mt-5 text-sm text-blue-100">
            {page.heroNote || "No pushy sales. A simple chat about what you need and what fits your budget."}
          </p>
        </PageHero>

        {/* PROBLEM */}
        <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="grid items-start gap-10 md:grid-cols-2">
            <div>
              <h2 className="mb-5 text-2xl font-bold tracking-tight text-brand-heading sm:text-3xl">
                {page.problemHeading || "What’s getting in the way"}
              </h2>

              {(page.problemParagraphs || []).map((p, idx) => (
                <p key={idx} className="mb-4 leading-relaxed text-brand-textSoft">
                  {p}
                </p>
              ))}
            </div>

            <div className="rounded-xl2 border border-brand-border bg-brand-bg p-6 sm:p-7">
              <h3 className="mb-4 text-lg font-semibold text-brand-heading">
                {page.problemBoxHeading || "Common signs"}
              </h3>
              <ul className="space-y-3 text-sm text-brand-text">
                {(page.problemBullets || []).map((b, idx) => (
                  <li key={idx} className="flex gap-3">
                    <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-primary" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* OFFER */}
        <section className="bg-brand-bg py-16 sm:py-20">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <h2 className="mb-4 text-center text-2xl font-bold tracking-tight text-brand-heading sm:text-3xl">
              {page.offerHeading || "What you get"}
            </h2>
            <p className="mx-auto mb-10 max-w-3xl text-center leading-relaxed text-brand-textSoft">
              {page.offerLead || ""}
            </p>

            <div className="grid gap-5 md:grid-cols-3">
              {(page.offerCards || []).slice(0, 6).map((c, idx) => (
                <div key={idx} className="rounded-xl2 border border-brand-border bg-white p-6 shadow-card">
                  <h3 className="mb-2 text-lg font-semibold text-brand-heading">{c.title}</h3>
                  <p className="text-sm leading-relaxed text-brand-textSoft">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* STEPS */}
        <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
          <h2 className="mb-10 text-center text-2xl font-bold tracking-tight text-brand-heading sm:text-3xl">
            {page.stepsHeading || "How it works"}
          </h2>

          <ol className="grid gap-4 text-sm sm:grid-cols-2 md:grid-cols-4">
            {(page.steps || []).slice(0, 4).map((s, idx) => (
              <li key={idx} className="rounded-xl2 border border-brand-border bg-white p-5 shadow-card">
                <p className="mb-1 text-xs font-semibold text-brand-primary">Step {idx + 1}</p>
                <h3 className="mb-2 font-semibold text-brand-heading">{s.title}</h3>
                <p className="leading-relaxed text-brand-textSoft">{s.text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* FINAL CTA */}
        <section className="bg-brand-navy px-4 py-16 text-center text-white sm:py-20">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-4 text-2xl font-bold tracking-tight sm:text-3xl">
              {page.finalHeading || "Ready to take the next step?"}
            </h2>
            <p className="mx-auto mb-8 max-w-2xl leading-relaxed text-blue-100">
              {page.finalLead || ""}
            </p>

            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to={CTA_TO}
                className={`inline-flex min-h-[48px] items-center justify-center rounded-full bg-white px-7 py-3 font-semibold text-brand-navy transition hover:bg-blue-50 focus-visible:ring-white focus-visible:ring-offset-brand-navy ${focusRing}`}
              >
                {CTA_PRIMARY}
              </Link>
              <Link
                to={CTA_SECONDARY_TO}
                className={`inline-flex min-h-[48px] items-center justify-center rounded-full border border-white/40 px-7 py-3 font-semibold text-white transition hover:bg-white/10 focus-visible:ring-white focus-visible:ring-offset-brand-navy ${focusRing}`}
              >
                {CTA_SECONDARY}
              </Link>
            </div>

            <p className="mt-8 text-sm leading-relaxed text-blue-100">
              You can also explore our{" "}
              <Link to="/services" className="font-medium text-white underline underline-offset-2 hover:text-brand-sky">services</Link>, learn more about{" "}
              <Link to="/web-design-adelaide" className="font-medium text-white underline underline-offset-2 hover:text-brand-sky">web design in Adelaide</Link>,{" "}
              <Link to="/website-landing" className="font-medium text-white underline underline-offset-2 hover:text-brand-sky">request a free website review</Link>, or{" "}
              <Link to="/contact" className="font-medium text-white underline underline-offset-2 hover:text-brand-sky">contact 2merative</Link>.
            </p>
          </div>
        </section>
      </div>
    </div>
  )
}
